import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Loader2, Mail } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const BATCH_SIZE = 5; // per API call — each dossier can take 5-10s

/**
 * BulkGenerateDossiersButton — for coaches / EPP staff.
 * Generates athlete dossiers in batches for athletes who don't have one yet.
 * Optionally emails each generated dossier to any linked parent accounts.
 */
export default function BulkGenerateDossiersButton() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [emailParents, setEmailParents] = useState(false);

  const loadStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/dossier/bulk-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch {
      // silent — button will just hide the counts
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const run = async () => {
    if (running) return;
    if (!status || status.athletes_without_dossier === 0) {
      toast.info('Every athlete already has a dossier. Nothing to do.');
      return;
    }
    const emailMsg = emailParents
      ? ` and email each one to the parents on file`
      : '';
    if (!window.confirm(
      `Generate dossiers for ${status.athletes_without_dossier} athletes${emailMsg}? This may take a few minutes.`
    )) return;

    setRunning(true);
    setProgress({ done: 0, total: status.athletes_without_dossier, failed: 0, emailed: 0 });
    const token = localStorage.getItem('token');
    let totalDone = 0;
    let totalFailed = 0;
    let totalEmailed = 0;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await axios.post(
          `${BACKEND_URL}/api/dossier/bulk-generate?limit=${BATCH_SIZE}&email_parents=${emailParents}`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
        );
        totalDone += res.data.processed_count || 0;
        totalFailed += res.data.failed_count || 0;
        totalEmailed += (res.data.processed || []).reduce((sum, p) => sum + (p.parents_emailed || 0), 0);
        setProgress({
          done: totalDone,
          total: totalDone + (res.data.remaining || 0),
          failed: totalFailed,
          emailed: totalEmailed,
        });
        if (res.data.done || (res.data.processed_count === 0 && res.data.failed_count === 0)) {
          break;
        }
      }
      const emailSummary = emailParents ? ` (${totalEmailed} emails sent)` : '';
      toast.success(`Generated ${totalDone} dossiers${totalFailed > 0 ? ` (${totalFailed} failed)` : ''}${emailSummary}.`);
    } catch (err) {
      console.error('Bulk generate failed:', err);
      toast.error(err.response?.data?.detail || 'Bulk generation failed. Check the server logs.');
    } finally {
      setRunning(false);
      await loadStatus();
    }
  };

  return (
    <div className="space-y-3" data-testid="bulk-generate-dossiers-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h3 className="text-sm font-bold uppercase tracking-widest text-white flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#02b5ff]" />
            Generate Missing Dossiers
          </h3>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            After a fresh CSV import, use this to create dossiers for every athlete who doesn&apos;t already have one.
            Existing dossiers are left untouched. Processing runs in small batches so progress is visible.
          </p>
          {status && (
            <p className="text-xs text-zinc-400 font-mono pt-1">
              {status.athletes_with_dossier} of {status.total_athletes} athletes have a dossier.
              {' '}<span className="text-[#02b5ff]">{status.athletes_without_dossier} pending</span>.
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <label
            className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none"
            data-testid="email-parents-toggle"
          >
            <Checkbox
              checked={emailParents}
              onCheckedChange={setEmailParents}
              disabled={running}
              className="border-zinc-600 data-[state=checked]:bg-[#02b5ff] data-[state=checked]:border-[#02b5ff]"
              data-testid="email-parents-checkbox"
            />
            <Mail className="h-3.5 w-3.5" />
            Also email parents
          </label>
          <Button
            onClick={run}
            disabled={running || !status || status.athletes_without_dossier === 0}
            className="h-11 px-5 bg-[#02b5ff]/15 hover:bg-[#02b5ff]/25 text-[#02b5ff] border border-[#02b5ff]/30 font-bold uppercase text-xs tracking-wider"
            data-testid="bulk-generate-dossiers-btn"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
            {running
              ? `Generating… (${progress?.done || 0}/${progress?.total || 0})`
              : 'Generate Missing Dossiers'}
          </Button>
        </div>
      </div>

      {progress && running && (
        <div className="text-xs font-mono text-zinc-400 bg-zinc-950/60 border border-[#02b5ff]/30 rounded p-3 grid grid-cols-4 gap-3" data-testid="bulk-generate-progress">
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Done</div><div className="text-emerald-300 text-lg">{progress.done}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Failed</div><div className="text-amber-300 text-lg">{progress.failed}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Emails Sent</div><div className="text-[#02b5ff] text-lg">{progress.emailed || 0}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Total</div><div className="text-white text-lg">{progress.total}</div></div>
        </div>
      )}
    </div>
  );
}
